import { LabelChip, Text } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { JsonObject, TrainingBudgetUnit } from "@/api";
import { hatcheryTextModes } from "@/app/theme";
import { HatTooltip } from "@/components/tooltip";
import { CHART_CHROME } from "@/shared/ui/chart/public";
import { EmptyState, stateFrameClassName } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import type { CheckpointTag } from "@/modules/training/public";
import { formatCompactCount } from "@/utils";
import { formatMetricNumber } from "../trainingSessionDetailFormat";
import type { SessionMetricsController } from "../useSessionMetrics";
import { metricInfluenceNotes } from "./metricConfigInfluence";
import { metricAxisUnit } from "./sessionAnalysisModel";
import styles from "./TrainingCurvesSection.module.css";

// 지표 토글 칩의 켜짐 색. 상태 칩(진행 중)과 같은 파랑이다. 꺼짐은 중립색이다(칩 색으로 on/off 를 표시한다).
const selectedChipColor = 0x2563eb;
const unselectedChipColor = 0x64748b;

type TrainingCurvesSectionProps = {
  metrics: SessionMetricsController;
  selectedKeys: readonly string[];
  onToggleKey: (key: string) => void;
  budgetUnit: TrainingBudgetUnit;
  // config 영향 툴팁이 읽는 제출 overrides.
  overrides: JsonObject | null;
  // 체크포인트 저장 시점의 세로 참조선. 축 단위가 태그 단위와 같은 차트에만 그린다.
  checkpointTags: readonly CheckpointTag[];
  live: boolean;
};

// 학습 곡선 카드 묶음. MLflow metric history 를 지표별 라인 차트로 그린다. 지표 목록은 서버가 주는
// metric_keys 로 동적으로 만든다. X축 라벨은 예산 단위에 맞춘다(step 예산 세션의 per-epoch 지표에
// "에폭" 라벨을 잘못 붙이지 않는다).
export function TrainingCurvesSection({
  metrics,
  selectedKeys,
  onToggleKey,
  budgetUnit,
  overrides,
  checkpointTags,
  live,
}: TrainingCurvesSectionProps): ReactNode {
  const { metricsState } = metrics;

  return (
    <section className={styles.card} aria-label="Training 곡선">
      <div className={styles.cardHeader}>
        <Text variant={hatcheryTextModes["caption-bold"]}>Training 곡선</Text>
        {live ? <span className={styles.cardMeta}>실시간 갱신 중 · 15초 간격</span> : null}
      </div>
      {metricsState.status === "idle" || metricsState.status === "loading" ? (
        <LoadingState size="sm">Training 지표를 불러오고 있습니다.</LoadingState>
      ) : metricsState.status === "error" ? (
        <EmptyState
          size="sm"
          tone="error"
          action={{ label: "다시 시도", onClick: metrics.refetch }}
        >
          Training 지표를 불러오지 못했습니다: {metricsState.message}
        </EmptyState>
      ) : metricsState.data.metric_keys.length === 0 ? (
        <EmptyState size="sm">
          아직 기록된 Training 지표가 없습니다. Training이 시작되면 여기에 표시됩니다.
        </EmptyState>
      ) : (
        <>
          <div className={styles.keyChips} role="group" aria-label="표시할 지표 선택">
            {metricsState.data.metric_keys.map((key) => {
              const pressed = selectedKeys.includes(key);
              return (
                <LabelChip
                  key={key}
                  color={pressed ? selectedChipColor : unselectedChipColor}
                  size="xs"
                  variant="interactive"
                  aria-pressed={pressed}
                  onClick={() => onToggleKey(key)}
                >
                  {key}
                </LabelChip>
              );
            })}
          </div>
          <div className={styles.charts}>
            {metricsState.data.metric_keys
              .filter((key) => selectedKeys.includes(key))
              .map((key) => (
                <MetricChartCard
                  key={key}
                  metricKey={key}
                  metrics={metrics}
                  budgetUnit={budgetUnit}
                  overrides={overrides}
                  checkpointTags={checkpointTags}
                />
              ))}
          </div>
        </>
      )}
    </section>
  );
}

function MetricChartCard({
  metricKey,
  metrics,
  budgetUnit,
  overrides,
  checkpointTags,
}: {
  metricKey: string;
  metrics: SessionMetricsController;
  budgetUnit: TrainingBudgetUnit;
  overrides: JsonObject | null;
  checkpointTags: readonly CheckpointTag[];
}): ReactNode {
  const historyState = metrics.histories[metricKey] ?? { status: "idle" as const };
  const axisUnit = metricAxisUnit(metricKey, budgetUnit);
  const unitLabel = axisUnit === "epoch" ? "EPOCH" : "STEP";
  const notes = metricInfluenceNotes(metricKey, overrides);
  const lastValue =
    metrics.metricsState.status === "success"
      ? (metrics.metricsState.data.last_values[metricKey] ?? null)
      : null;

  return (
    <div className={styles.chartCard}>
      <div className={styles.chartHead}>
        <span className={styles.chartTitle}>
          {metricKey}
          {notes.length > 0 ? (
            <HatTooltip
              content={
                <div className={styles.influenceNotes}>
                  {notes.map((note) => (
                    <p key={note}>{note}</p>
                  ))}
                </div>
              }
            >
              <span
                className={styles.influenceTrigger}
                aria-label={`${metricKey} 관련 config 안내`}
              >
                ⓘ
              </span>
            </HatTooltip>
          ) : null}
        </span>
        <span className={styles.cardMeta}>
          {lastValue !== null ? `최근 ${formatMetricNumber(lastValue)} · ` : ""}X축 {unitLabel}
        </span>
      </div>
      {historyState.status === "success" && historyState.data.points.length > 0 ? (
        <MetricLineChart
          metricKey={metricKey}
          points={historyState.data.points}
          unitLabel={unitLabel}
          referenceSteps={checkpointTags
            .filter((tag) => tag.unit === axisUnit)
            .map((tag) => tag.value)}
        />
      ) : (
        <div className={`${stateFrameClassName} ${styles.chartState}`}>
          {historyState.status === "error" ? (
            <EmptyState
              size="sm"
              mode="plain"
              tone="error"
              align="center"
              action={{ label: "다시 시도", onClick: metrics.refetch }}
            >
              시계열을 불러오지 못했습니다: {historyState.message}
            </EmptyState>
          ) : historyState.status === "success" ? (
            <EmptyState size="sm" mode="plain">
              이 지표의 기록이 아직 없습니다.
            </EmptyState>
          ) : (
            <LoadingState size="sm" mode="plain">
              시계열을 불러오고 있습니다.
            </LoadingState>
          )}
        </div>
      )}
    </div>
  );
}

function MetricLineChart({
  metricKey,
  points,
  unitLabel,
  referenceSteps,
}: {
  metricKey: string;
  points: readonly { step: number; value: number }[];
  unitLabel: string;
  referenceSteps: readonly number[];
}): ReactNode {
  return (
    <ResponsiveContainer width="100%" height={200}>
      <LineChart data={[...points]} margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
        <CartesianGrid stroke={CHART_CHROME.grid} vertical={false} />
        <XAxis
          dataKey="step"
          type="number"
          domain={["dataMin", "dataMax"]}
          stroke={CHART_CHROME.axis}
          fontSize={11}
          tickFormatter={(tick) => formatCompactCount(Number(tick))}
        />
        <YAxis
          width={56}
          domain={["auto", "auto"]}
          stroke={CHART_CHROME.axis}
          fontSize={11}
          tickFormatter={(tick) => formatMetricNumber(Number(tick))}
        />
        <Tooltip
          labelFormatter={(step) => `${unitLabel} ${formatMetricNumber(Number(step))}`}
          formatter={(value) => [formatMetricNumber(Number(value)), metricKey]}
          contentStyle={{
            background: CHART_CHROME.tooltipBg,
            borderColor: CHART_CHROME.tooltipBorder,
          }}
        />
        {referenceSteps.map((step) => (
          // 체크포인트 저장 시점. 이 세로선 위치의 policy 를 체크포인트 탭에서 받을 수 있다.
          <ReferenceLine key={step} x={step} stroke={CHART_CHROME.grid} strokeDasharray="4 4" />
        ))}
        <Line
          dataKey="value"
          type="monotone"
          stroke="var(--c6-color-text-accent)"
          strokeWidth={1.5}
          dot={false}
          isAnimationActive={false}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}
